import * as HttpStatus from 'http-status-codes';
import { ECW_PATTERN, ECM_PATTERN, CORRESPONDENCE_PATTERN } from '../constants';
import { Service } from '../service.enum';
import { ResponseError } from '../../ResponseError';
import Logging from '../../../Logging/Logging';
import { HttpMethod } from '../../../..';

type HttpHeaders = {
  authorization: string;
  method: HttpMethod;
  'Access-Control-Allow-Origin': string;
}

export default class FetchWrapper {
  private accessToken: string;
  private serviceMap: Map<Service, string>;
  private headers: HttpHeaders;

  constructor(gatewayEndPoint: string, accessToken: string) {
    this.serviceMap = new Map<Service, string>();

    this.serviceMap.set(Service.GATEWAY, `${gatewayEndPoint}`);
    this.serviceMap.set(Service.ECW, `${gatewayEndPoint}/${ECW_PATTERN}`);
    this.serviceMap.set(Service.ECM, `${gatewayEndPoint}/${ECM_PATTERN}`);
    this.serviceMap.set(Service.CORRESPONDENCE, `${gatewayEndPoint}/${CORRESPONDENCE_PATTERN}`);

    this.accessToken = accessToken;
    this.headers = {
      method: HttpMethod.GET,
      authorization: `Bearer ${accessToken}`,
      'Access-Control-Allow-Origin': '*',
    };
  }

  // Parse the Error Response into a ResponseError object
  static parseErrorResponse(method: HttpMethod, url: string, response: Response): Promise<ResponseError> {
    // Read the Response Body as Text
    return response.text().then((text: string) => {
      try {
        // Parse the Body Text as Json
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const json: any = text ? JSON.parse(text) : null;

        const error: ResponseError = {
          url,
          method,
          statusCode: response.status,
          statusDescription: HttpStatus.getStatusText(response.status),
          statusCodeDescription: `${response.status} ${HttpStatus.getStatusText(response.status)}`,
          responseBody: json, // The whole Body from Error Response
          message: json ? json.message || json.error?.message : null, // Not sure if the 'Message' is consistent in all Error Responses
          exception: json ? json.exception : null, // Not sure if the 'Exception' is consistent in all Error Responses
        };

        return error;
      } catch (e) {
        // The Response Body is Text
        const error: ResponseError = {
          url,
          method,
          statusCode: response.status,
          statusDescription: HttpStatus.getStatusText(response.status),
          statusCodeDescription: `${response.status} ${HttpStatus.getStatusText(response.status)}`,
          responseBody: text, // The whole Body from Error Response
          message: null,
          exception: null,
        };

        return error;
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async handleFetchErrors(method: HttpMethod, url: string, response: Response): Promise<any> {
    // Not Success (200)
    if (!response.ok) {
      const responseError: ResponseError = await this.parseErrorResponse(method, url, response);

      Logging.Error('Response Error:', responseError);

      // Throw the Response Error
      throw responseError;
    } else {
      // Return the Original Response
      return response;
    }
  }

  static async extractHeaders(response: Response): Promise<Headers> {
    return response.headers;
  }

  get<T>(url: string): Promise<T> {
    return fetch(url, {
      headers: this.headers,
      mode: 'cors',
      credentials: 'include',
    }).then((response) => FetchWrapper.handleFetchErrors(HttpMethod.GET, url, response))
      .then((response) => response.text())
      .then((text) => (text ? JSON.parse(text) : null))
      .then((json) => (json as unknown as T));
  }

  post<T>(url: string, body: T): Promise<T> {
    return fetch(url, {
      headers: {
        ...this.headers,
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      method: HttpMethod.POST,
      body: JSON.stringify(body),
    }).then((response: Response) => FetchWrapper.handleFetchErrors(HttpMethod.POST, url, response))
      .then((response: Response) => response.text())
      .then((text: string) => (text ? JSON.parse(text) : null))
      .then((json) => json as unknown as T);
  }

  postReturnHeaders<T>(url: string, body: T): Promise<Headers> {
    return fetch(url, {
      headers: {
        ...this.headers,
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      method: HttpMethod.POST,
      body: JSON.stringify(body),
    }).then((response: Response) => FetchWrapper.handleFetchErrors(HttpMethod.POST, url, response))
      .then((response: Response) => FetchWrapper.extractHeaders(response));
  }

  put<T>(url: string, body: T): Promise<T> {
    return fetch(url, {
      headers: {
        ...this.headers,
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      method: HttpMethod.PUT,
      body: JSON.stringify(body),
    }).then((response) => FetchWrapper.handleFetchErrors(HttpMethod.PUT, url, response))
      .then((response) => response.text())
      .then((text) => (text ? JSON.parse(text) : null))
      .then((json) => json as unknown as T);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  patch<T>(url: string, body: any): Promise<T> {
    const today = new Date();

    return fetch(url, {
      headers: {
        ...this.headers,
        'Content-Type': 'application/json',
        'x-client_date_time': today.toISOString(),
      },
      mode: 'cors',
      method: HttpMethod.PATCH,
      body: JSON.stringify(body),
    }).then((response) => FetchWrapper.handleFetchErrors(HttpMethod.PATCH, url, response))
      .then((response) => response.text())
      .then((text) => (text ? JSON.parse(text) : null))
      .then((json) => json as unknown as T);
  }

  del<T>(url: string): Promise<T> {
    return fetch(url, {
      headers: {
        ...this.headers,
      },
      mode: 'cors',
      method: HttpMethod.DELETE,
    }).then((response) => FetchWrapper.handleFetchErrors(HttpMethod.DELETE, url, response))
      .then((response) => response.text())
      .then((text) => (text ? JSON.parse(text) : null))
      .then((json) => json as unknown as T);
  }

  getFromService<T>(service: Service, url: string): Promise<T> {
    return this.get<T>(`${this.serviceMap.get(service)}/${url}`);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  postToService<T>(service: Service, url: string, body: any): Promise<T> {
    return this.post<T>(`${this.serviceMap.get(service)}/${url}`, body);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  postToServiceReturnHeaders<T>(service: Service, url: string, body: any): Promise<Headers> {
    return this.postReturnHeaders<Headers>(`${this.serviceMap.get(service)}/${url}`, body);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  putToService<T>(service: Service, url: string, body: any): Promise<T> {
    return this.put<T>(`${this.serviceMap.get(service)}/${url}`, body);
  }

  delFromService<T>(service: Service, url: string): Promise<T> {
    return this.del<T>(`${this.serviceMap.get(service)}/${url}`);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  patchToService<T>(service: Service, url: string, body: any): Promise<T> {
    return this.patch<T>(`${this.serviceMap.get(service)}/${url}`, body);
  }
}
